import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Check, Clock, RefreshCw, DollarSign } from 'lucide-react';
import { AdminLayout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getAllPayments, validatePayment, refundPayment, getAdminMe } from '@/lib/api';

const AdminPayments = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterMethod, setFilterMethod] = useState('all');
  const [refundDialog, setRefundDialog] = useState({ open: false, payment: null });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      await getAdminMe();
      const response = await getAllPayments();
      setPayments(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async (paymentId) => {
    try {
      await validatePayment(paymentId);
      toast.success('Paiement validé');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handleRefund = async () => {
    if (!refundDialog.payment) return;
    try {
      await refundPayment(refundDialog.payment.id);
      toast.success('Remboursement effectué');
      setRefundDialog({ open: false, payment: null });
      loadData();
    } catch (error) {
      toast.error('Erreur lors du remboursement');
    }
  };

  const filteredPayments = payments.filter((p) => {
    if (filterStatus !== 'all' && p.status !== filterStatus) return false;
    if (filterMethod !== 'all' && p.payment_method !== filterMethod) return false;
    return true;
  });

  const totalCollected = payments.filter(p => p.status === 'paid').reduce((sum, p) => sum + (p.amount || 0), 0);
  const totalPending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + (p.amount || 0), 0);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid':
        return <Badge className="bg-green-100 text-green-800"><Check className="w-3 h-3 mr-1" /> Payé</Badge>;
      case 'refunded':
        return <Badge className="bg-purple-100 text-purple-800"><RefreshCw className="w-3 h-3 mr-1" /> Remboursé</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-800"><Clock className="w-3 h-3 mr-1" /> En attente</Badge>;
    }
  };

  const getMethodLabel = (method) => {
    switch (method) {
      case 'paypal': return 'PayPal';
      case 'paylib': return 'Paylib';
      case 'virement': return 'Virement';
      default: return method;
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Gestion des paiements">
        <div className="flex items-center justify-center h-64">
          <div className="animate-pulse text-gray-500">Chargement...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestion des paiements">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-sm text-gray-500">Total collecté</p>
              <p className="text-2xl font-bold text-green-600">{totalCollected}€</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-amber-500" />
            <div>
              <p className="text-sm text-gray-500">En attente</p>
              <p className="text-2xl font-bold text-amber-600">{totalPending}€</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-terracotta" />
            <div>
              <p className="text-sm text-gray-500">Paiements</p>
              <p className="text-2xl font-bold">{payments.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="w-full md:w-40">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="paid">Payé</SelectItem>
              <SelectItem value="refunded">Remboursé</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterMethod} onValueChange={setFilterMethod}>
            <SelectTrigger data-testid="filter-method" className="w-full md:w-40">
              <SelectValue placeholder="Méthode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les méthodes</SelectItem>
              <SelectItem value="paypal">PayPal</SelectItem>
              <SelectItem value="paylib">Paylib</SelectItem>
              <SelectItem value="virement">Virement</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Famille</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Méthode</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    Aucun paiement
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayments.map((payment) => (
                  <TableRow key={payment.id}>
                    <TableCell className="font-medium">
                      {payment.rsvp?.family_name || '-'}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {payment.rsvp?.email || '-'}
                    </TableCell>
                    <TableCell className="text-right font-bold">
                      {payment.amount}€
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getMethodLabel(payment.payment_method)}</Badge>
                    </TableCell>
                    <TableCell>{getStatusBadge(payment.status)}</TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {new Date(payment.created_at).toLocaleDateString('fr-FR')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {payment.status === 'pending' && (
                          <Button
                            data-testid={`btn-validate-${payment.id}`}
                            size="sm"
                            onClick={() => handleValidate(payment.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" /> Valider
                          </Button>
                        )}
                        {payment.status === 'paid' && (
                          <Button
                            data-testid={`btn-refund-${payment.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => setRefundDialog({ open: true, payment })}
                            className="text-purple-600 border-purple-600 hover:bg-purple-50"
                          >
                            <RefreshCw className="w-4 h-4 mr-1" /> Rembourser
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <p className="text-sm text-gray-500 mt-4">
        {filteredPayments.length} paiement(s)
      </p>

      {/* Refund Dialog */}
      <Dialog open={refundDialog.open} onOpenChange={(open) => setRefundDialog({ open, payment: refundDialog.payment })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer le remboursement ?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            Êtes-vous sûr de vouloir rembourser <strong>{refundDialog.payment?.amount}€</strong> à{' '}
            <strong>{refundDialog.payment?.rsvp?.family_name}</strong> ?
          </p>
          <p className="text-sm text-gray-500">
            Un email de confirmation sera envoyé automatiquement.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundDialog({ open: false, payment: null })}>
              Annuler
            </Button>
            <Button data-testid="btn-confirm-refund" onClick={handleRefund} className="bg-purple-600 hover:bg-purple-700">
              Confirmer le remboursement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminPayments;
